import React, { createContext, ReactNode, useContext, useState } from 'react';

import { RetrieveUserGroupSchema } from '@app/models/users';

import { defaultColumns, NEW_GROUP_ID } from './constants';

interface IContext {
  newGroups: RetrieveUserGroupSchema[];
  columns: any[];
  expandedRowKeys: React.Key[];
  editingRowKey: React.Key | null;
  selectedRows: React.Key[];
  setExpandedRowKeys: (rows: React.Key[]) => void;
  setSelectedRows: (rows: React.Key[]) => void;
  onAddRow: () => void;
  onEditRow: (rowKey: React.Key) => void;
  onEditEnd: (rowKey: React.Key) => void;
  onCancelEdit: (rowKey: React.Key) => void;
  onSaveSuccess: () => void;
}

const GroupContext = createContext<IContext>(null!);

function GroupProvider({ children }: { children: ReactNode }) {
  const [newGroups, setNewGroups] = useState<RetrieveUserGroupSchema[]>([]);
  const [expandedRowKeys, setExpandedRowKeys] = React.useState<React.Key[]>([]);
  const [editingRowKey, setEditingRowKey] = React.useState<React.Key | null>(null);
  const [selectedRows, setSelectedRows] = React.useState<React.Key[]>([]);

  const handleAddRow = () => {
    if (editingRowKey) {
      return;
    }

    const newData: RetrieveUserGroupSchema = {
      uid: NEW_GROUP_ID,
      name: '',
      cluster: null,
      total_users: 0,
      users: [],
      $permissions: {},
    };
    setNewGroups([newData, ...newGroups]);
    setEditingRowKey(NEW_GROUP_ID);
  };

  const handleSave = () => {
    setNewGroups([]);
  };

  const handleEditRow = (rowKey: React.Key) => {
    setEditingRowKey(rowKey);
  };

  const handleEditEnd = (rowKey: React.Key) => {
    setEditingRowKey((prev) => (prev === rowKey ? null : prev));
  };

  const handleCancelEdit = (rowKey: React.Key) => {
    if (rowKey === NEW_GROUP_ID) {
      setNewGroups((prev) => prev.filter((group) => group.uid !== rowKey));
      setEditingRowKey((prev) => (prev === rowKey ? null : prev));
      return;
    }

    handleEditEnd(rowKey);
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: RetrieveUserGroupSchema) => ({
        record,
        editable: col.editable && editingRowKey === record.uid,
        dataIndex: col.dataIndex,
        title: col.title,
        required: col.required,
      }),
    };
  });

  const value = {
    newGroups,
    columns,
    onAddRow: handleAddRow,
    onEditRow: handleEditRow,
    onEditEnd: handleEditEnd,
    onCancelEdit: handleCancelEdit,
    onSaveSuccess: handleSave,
    editingRowKey,
    expandedRowKeys,
    setExpandedRowKeys,
    selectedRows,
    setSelectedRows,
  };
  return <GroupContext.Provider value={value}>{children}</GroupContext.Provider>;
}

const useGroups = () => {
  const context = useContext(GroupContext);

  if (context === undefined) {
    throw new Error('useProviderState must be used within a GroupProvider');
  }

  return context;
};

export { GroupContext, GroupProvider, useGroups };
